import { Dispatch, SetStateAction, memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Modal,
  Radio,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  Accordion,
  Typography,
  IconButton,
  RadioGroup,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { IRole, IPermission } from '../../../../../shared/types/api/user.type'
import { useToast } from '../../../../contexts/toast.context'
import { resources } from '../../../../utils/constants'
import { createRole, deleteRole, updateRole } from '../../../../services/user.service'
import { AuthContext } from '../../../../contexts/auth.context'
import { ScopeFieldEditor } from '../../Users/PermissionScopeEditor'
import { IPermissionScope } from '../../../../../shared/types/api/user.type'

import styles from './styles.module.scss'

interface IProps<T> {
  open: boolean
  onClose: () => void
  isLightMode: boolean
  rolesWithPermissions: IRole[]
  setRolesWithPermissions: Dispatch<SetStateAction<IRole[]>>
}

const actionTypes = ['read', 'write']

const isTempId = (id: string) => id.startsWith('new-')
const makeTempId = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const SAVE_DEBOUNCE_MS = 400

// Find the (single) populated scope key, or null if unscoped.
const scopeKey = (scope: unknown): string | null => {
  if (!scope || typeof scope !== 'object') return null
  for (const [k, v] of Object.entries(scope as Record<string, unknown>)) {
    if (v != null && String(v).trim() !== '') return k
  }
  return null
}

// Numeric tail of a scope key (f200 → 200). Unscoped sorts first.
const scopeOrdinal = (key: string | null): number => {
  if (!key) return -Infinity
  const m = key.match(/\d+/)
  return m ? parseInt(m[0], 10) : 0
}

// Sort comparator parameterized by which ids count as "added during this session".
// Those ids stay anchored at the bottom of the list until the role's accordion is
// closed and reopened (or the modal closes), at which point the caller passes a
// fresh snapshot and they rejoin the normal alphabetical order.
const makeComparePermissions = (isFresh: (id: string) => boolean) => (a: IPermission, b: IPermission) => {
  const aFresh = isFresh(a.id)
  const bFresh = isFresh(b.id)
  if (aFresh && !bFresh) return 1
  if (!aFresh && bFresh) return -1
  // Both rows are fresh — preserve the order they were added in. Array.sort
  // is stable (ES2019+), so returning 0 keeps their relative position from
  // role.permissions, which handleAddPermission always appends to.
  if (aFresh && bFresh) return 0
  if (a.resource_type !== b.resource_type) return a.resource_type.localeCompare(b.resource_type)
  if (a.action !== b.action) return a.action === 'read' ? -1 : 1
  return scopeOrdinal(scopeKey(a.scope)) - scopeOrdinal(scopeKey(b.scope))
}

interface PermissionRowProps {
  roleIndex: number
  roleId: string
  permission: IPermission
  onChange: (roleIndex: number, permId: string, key: string, value: boolean | string | IPermissionScope | null) => void
  onDelete: (roleId: string, permId: string) => void
}

const PermissionRow = memo(({ roleIndex, roleId, permission, onChange, onDelete }: PermissionRowProps) => {
  const onResourceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(roleIndex, permission.id, 'resource_type', e.target.value),
    [onChange, roleIndex, permission.id],
  )
  const onActionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(roleIndex, permission.id, 'action', e.target.value),
    [onChange, roleIndex, permission.id],
  )
  const onScopeChange = useCallback(
    (value: IPermissionScope) => onChange(roleIndex, permission.id, 'scope', value),
    [onChange, roleIndex, permission.id],
  )
  const onDeleteClick = useCallback(() => onDelete(roleId, permission.id), [onDelete, roleId, permission.id])

  return (
    <Box className={styles.item}>
      <Box className={styles.permissionRow}>
        <TextField
          select
          label="Resource Type"
          value={permission.resource_type}
          onChange={onResourceChange}
          className={styles.permissionInput}
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="" disabled>
            <em>Select…</em>
          </MenuItem>
          {resources.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <RadioGroup value={permission.action} onChange={onActionChange}>
          <Box sx={{ display: 'flex' }}>
            {actionTypes.map((act) => (
              <FormControlLabel key={act} value={act} control={<Radio />} label={act} />
            ))}
          </Box>
        </RadioGroup>

        <ScopeFieldEditor scope={permission.scope} onChange={onScopeChange} />

        <IconButton size="small" onClick={onDeleteClick}>
          <DeleteIcon sx={{ color: 'red' }} fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )
})

interface RolePermissionListProps {
  role: IRole
  roleIndex: number
  onChange: PermissionRowProps['onChange']
  onDelete: PermissionRowProps['onDelete']
}

// Renders one role's permission rows. Captures the set of permission ids that
// existed at mount time so anything added during this session (still-temp rows,
// and rows that have since had their temp ids swapped for real ones) stays
// anchored at the bottom of the list. The Accordion uses `unmountOnExit`, so
// collapsing/reopening the role — or closing/reopening the modal — remounts
// this component and re-snapshots, naturally letting the new rows rejoin the
// alphabetical sort.
const RolePermissionList = ({ role, roleIndex, onChange, onDelete }: RolePermissionListProps) => {
  const originalIdsRef = useRef<Set<string>>(new Set(role.permissions.map((p) => p.id)))
  const compare = makeComparePermissions((id) => !originalIdsRef.current.has(id))

  return (
    <>
      {[...role.permissions].sort(compare).map((permission) => (
        <PermissionRow
          key={permission.id}
          roleIndex={roleIndex}
          roleId={role.id}
          permission={permission}
          onChange={onChange}
          onDelete={onDelete}
        />
      ))}
    </>
  )
}

const RolesModal = <T extends Record<string, any>>({
  open,
  onClose,
  isLightMode,
  rolesWithPermissions,
  setRolesWithPermissions,
}: IProps<T>) => {
  const { showToast } = useToast()
  const [pendingRole, setPendingRole] = useState<string>('')

  const { getUser } = useContext(AuthContext)

  // Mirror the latest rolesWithPermissions into a ref so the debounced save can
  // read the freshest local state at the moment the timer fires (rather than the
  // snapshot from the keystroke that scheduled it).
  const rolesRef = useRef(rolesWithPermissions)
  useEffect(() => {
    rolesRef.current = rolesWithPermissions
  }, [rolesWithPermissions])

  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
  // Per-role monotonic counter; responses with a stale counter are dropped so
  // an in-flight save can't clobber a newer local edit.
  const seqRef = useRef<Record<number, number>>({})

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const t of Object.values(timers)) clearTimeout(t)
    }
  }, [])

  const persistRole = useCallback(
    async (roleIndex: number) => {
      const role = rolesRef.current[roleIndex]
      if (!role) return

      // Don't send half-filled temp rows; they'd fail server-side. Other rows
      // for this role can still be saved.
      const sendPerms = role.permissions.filter((p) => !isTempId(p.id) || (p.resource_type && p.action))
      const sentTempPerms = sendPerms.filter((p) => isTempId(p.id))

      // Snapshot real ids we knew about at send time so we can identify
      // newly-created perms in the response.
      const knownRealIdsAtSend = new Set(sendPerms.filter((p) => !isTempId(p.id)).map((p) => String(p.id)))

      const mySeq = (seqRef.current[roleIndex] ?? 0) + 1
      seqRef.current[roleIndex] = mySeq

      const resp = await updateRole(role.id, { name: role.name, permissions: sendPerms })

      // Newer save has been scheduled (or completed) since we fired — discard
      // this response so it can't roll back what the user typed in the meantime.
      if (seqRef.current[roleIndex] !== mySeq) return

      if (!resp.success) {
        showToast('error', resp.message || 'Failed to save permission')
        return
      }

      // For pure real-id edits, local state is authoritative — nothing to apply.
      if (!resp.data || sentTempPerms.length === 0) return

      // Match sent temp rows to newly-created perms in the response by field
      // combo (resource_type + action + scope) at send time. Swap just the id
      // in local state — preserve everything else (including in-flight edits
      // and other temp rows still being filled in).
      const sigOf = (p: IPermission) => `${p.resource_type}|${p.action}|${JSON.stringify(p.scope ?? {})}`
      const newPermsBySig = new Map<string, IPermission>()
      for (const p of resp.data.permissions) {
        if (knownRealIdsAtSend.has(String(p.id))) continue
        const k = sigOf(p)
        if (!newPermsBySig.has(k)) newPermsBySig.set(k, p)
      }

      const tempIdToRealId = new Map<string, string>()
      for (const sent of sentTempPerms) {
        const match = newPermsBySig.get(sigOf(sent))
        if (match) {
          tempIdToRealId.set(sent.id, String(match.id))
          newPermsBySig.delete(sigOf(sent))
        }
      }

      if (tempIdToRealId.size === 0) return

      setRolesWithPermissions((prev) =>
        prev.map((r, i) => {
          if (i !== roleIndex) return r
          return {
            ...r,
            permissions: r.permissions.map((p) => {
              const realId = tempIdToRealId.get(p.id)
              return realId ? { ...p, id: realId } : p
            }),
          }
        }),
      )
    },
    [setRolesWithPermissions, showToast],
  )

  const scheduleSave = useCallback(
    (roleIndex: number) => {
      const existing = timersRef.current[roleIndex]
      if (existing) clearTimeout(existing)
      timersRef.current[roleIndex] = setTimeout(() => {
        delete timersRef.current[roleIndex]
        persistRole(roleIndex)
      }, SAVE_DEBOUNCE_MS)
    },
    [persistRole],
  )

  const handleRenameRole = (index: number, name: string) => {
    setRolesWithPermissions((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], name }
      return updated
    })
  }

  const handlePermissionChange = useCallback(
    (roleIndex: number, permId: string, key: string, value: boolean | string | IPermissionScope | null) => {
      setRolesWithPermissions((prev) => {
        const role = prev[roleIndex]
        if (!role) return prev
        const nextPerms = role.permissions.map((p) => (p.id === permId ? { ...p, [key]: value } : p))
        const updated = [...prev]
        updated[roleIndex] = { ...role, permissions: nextPerms }
        return updated
      })
      scheduleSave(roleIndex)
    },
    [scheduleSave, setRolesWithPermissions],
  )

  const handleAddPermission = (roleIndex: number) => {
    setRolesWithPermissions((prev) => {
      const updated = [...prev]
      updated[roleIndex] = {
        ...updated[roleIndex],
        permissions: [
          ...updated[roleIndex].permissions,
          { id: makeTempId(), resource_type: '', action: '', scope: {} } as IPermission,
        ],
      }
      return updated
    })
  }

  const handleDeletePermission = useCallback(
    async (roleId: string, permId: string) => {
      // Read from the ref so a delete that fires between a permission edit and
      // its debounced save doesn't send pre-edit data and revert the edit.
      const role = rolesRef.current.find((r) => r.id === roleId)
      if (!role) return
      const nextPermissions = role.permissions.filter((p) => p.id !== permId)

      // Unsaved row — just drop it from local state.
      if (isTempId(permId)) {
        setRolesWithPermissions((prev) =>
          prev.map((r) => (r.id === roleId ? { ...r, permissions: nextPermissions } : r)),
        )
        return
      }

      const resp = await updateRole(roleId, { name: role.name, permissions: nextPermissions })
      if (resp.success && resp.data) {
        setRolesWithPermissions((prev) => prev.map((r) => (r.id === roleId ? resp.data! : r)))
      }
    },
    [setRolesWithPermissions],
  )

  const handleAddRole = async () => {
    const name = pendingRole.trim()
    if (!name) {
      showToast('error', 'Role name is required')
      return
    }
    const resp = await createRole({ name })
    if (resp.success && resp.data) {
      setRolesWithPermissions((prev) => [...prev, resp.data!])
      setPendingRole('')
    }
  }

  const handleDeleteRole = async (index: number) => {
    const role = rolesWithPermissions[index]
    if (!role) return
    const resp = await deleteRole(role.id)
    if (resp.success) {
      setRolesWithPermissions((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async () => {
    // Permission edits already auto-save via the debounce; cancel any pending
    // timers so they can't re-fire with stale state after the explicit flush.
    for (const t of Object.values(timersRef.current)) clearTimeout(t)
    timersRef.current = {}
    // Bump every role's sequence so any in-flight debounced responses are dropped.
    for (const idx of Object.keys(seqRef.current)) {
      seqRef.current[Number(idx)] = (seqRef.current[Number(idx)] ?? 0) + 1
    }

    const results = await Promise.all(
      rolesWithPermissions.map((role) => {
        // Match persistRole: drop half-filled temp rows that would fail server validation.
        const sendPerms = role.permissions.filter((p) => !isTempId(p.id) || (p.resource_type && p.action))
        return updateRole(role.id, { name: role.name, permissions: sendPerms })
      }),
    )
    if (results.every((r) => r.success)) {
      getUser()
      showToast('success', 'Roles and permissions updated')
      setRolesWithPermissions(results.map((r) => r.data!))
    }
  }

  return (
    <Modal className={styles.modal} open={open} onClose={onClose}>
      <Box className={styles.modalContent}>
        <Box className={styles.content}>
          <Box
            className={styles.header}
            sx={{
              borderBottom: isLightMode ? '1px solid black' : '1px solid white',
            }}
          >
            <Typography className={styles.title}>Roles and Permissions</Typography>
            <IconButton onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>

          <Box className={styles.header} sx={{ marginTop: 5 }}>
            <TextField
              label="New Role Name"
              value={pendingRole}
              onChange={(e) => setPendingRole(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRole()}
              size="small"
              className={styles.roleSelect}
            />
            <Button onClick={handleAddRole} className={styles.roleBtn}>
              Add Role
              <AddIcon />
            </Button>
          </Box>

          <Box className={styles.rolesList}>
            {rolesWithPermissions.map((role, roleIndex) => (
              <Accordion key={role.id} TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <TextField
                    value={role.name}
                    onChange={(e) => handleRenameRole(roleIndex, e.target.value)}
                    size="small"
                    className={styles.roleSelect}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteRole(roleIndex)
                    }}
                  >
                    <DeleteIcon sx={{ color: 'red' }} fontSize="small" />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails className={styles.permissions}>
                  <RolePermissionList
                    role={role}
                    roleIndex={roleIndex}
                    onChange={handlePermissionChange}
                    onDelete={handleDeletePermission}
                  />
                  <Button
                    className={styles.addPermissionBtn}
                    onClick={() => handleAddPermission(roleIndex)}
                    size="small"
                  >
                    Add Permission
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          <Box className={styles.submitBtnContainer}>
            <Button onClick={handleSubmit}>Submit</Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default RolesModal
